import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Cake, Flower2, Candy, Gift, Sparkles, Truck, ShieldCheck, CreditCard, HeartHandshake, PackageCheck, Clock3 } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import FlashSaleSection from "../components/FlashSaleSection";

const HERO_IMG = "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?q=80&w=900&auto=format&fit=crop";
const HERO_IMG_2 = "https://images.pexels.com/photos/19258787/pexels-photo-19258787.jpeg?auto=compress&cs=tinysrgb&w=600";

const SHORTCUTS = [
  { label: "Birthday Gifts", icon: Cake, to: "/shop?occasion=birthday" },
  { label: "Anniversary Gifts", icon: HeartHandshake, to: "/shop?occasion=anniversary" },
  { label: "Valentine's Gifts", icon: Sparkles, to: "/shop?occasion=valentine" },
  { label: "Flowers", icon: Flower2, to: "/shop?category=flowers" },
  { label: "Chocolates", icon: Candy, to: "/shop?category=chocolates" },
  { label: "Gift Hampers", icon: Gift, to: "/shop?category=gift-hampers" },
  { label: "Personalized", icon: Sparkles, to: "/shop?category=personalized-gifts" },
  { label: "Same Day Delivery", icon: Truck, to: "/shop?delivery=same_day" },
];

const MARQUEE_ITEMS = ["Hand-wrapped with satin & care", "Same-day delivery in metro cities", "Your photos & messages, beautifully printed", "Personalised notes with every gift", "Crafted keepsakes that last"];

const MANIFESTO = [
  { n: "01", title: "Curated with care", text: "Every gift in our collection is handpicked for the feeling it creates — not just the shelf it fills." },
  { n: "02", title: "Wrapped to delight", text: "Signature ribbons, hand-written notes and packaging so beautiful it becomes part of the gift." },
  { n: "03", title: "Delivered with love", text: "From same-day surprises to midnight smiles, we treat every delivery like it's for our own family." },
];

const TRUST = [
  { icon: ShieldCheck, label: "Secure Checkout" },
  { icon: Truck, label: "Reliable Delivery" },
  { icon: CreditCard, label: "Multiple Payment Options" },
  { icon: HeartHandshake, label: "Easy Support" },
  { icon: PackageCheck, label: "Carefully Packed Gifts" },
];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
        {children}
      </motion.span>
    </span>
  );
}

function SectionHead({ overline, title, sub, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">{overline}</p>
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-3 tracking-tight">{title}</h2>
        {sub && <p className="text-stone-500 mt-2 max-w-lg">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { recent } = useShop();
  const [meta, setMeta] = useState({ categories: [], occasions: [] });
  const [trending, setTrending] = useState([]);
  const [best, setBest] = useState([]);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, -40]);
  const y2 = useTransform(scrollY, [0, 500], [0, 50]);

  useEffect(() => {
    (async () => {
      try {
        const [m, t, b] = await Promise.all([
          api.get("/meta"),
          api.get("/products", { params: { sort: "popular", limit: 4 } }),
          api.get("/products", { params: { tag: "bestseller", limit: 4 } }),
        ]);
        setMeta(m.data);
        setTrending(t.data.items);
        setBest(b.data.items);
      } catch {
        /* sections render empty gracefully */
      }
    })();
  }, []);

  return (
    <div className="bg-[#FDFBF7]" data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-12 lg:pt-20 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5EFE6] border border-[#E9E2D7] text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]" data-testid="hero-overline">
              <Sparkles size={13} /> Make Every Moment Gift-Worthy
            </motion.p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[4.2rem] font-bold text-stone-900 leading-[1.05] tracking-tight mt-6" data-testid="hero-heading">
              <MaskedLine delay={0.15}>Find the Perfect Gift</MaskedLine>
              <MaskedLine delay={0.3}>for <span className="italic text-[#B45309]">Every</span> Moment</MaskedLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="text-stone-500 text-base sm:text-lg leading-relaxed mt-6 max-w-md">
              Thoughtful gifts for birthdays, anniversaries, celebrations and every special moment — wrapped beautifully, delivered on time.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="flex flex-wrap gap-4 mt-8">
              <Link to="/shop" className="inline-flex items-center gap-2 h-13 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] hover:-translate-y-0.5 hover:shadow-lg transition-[background-color,transform,box-shadow] duration-300" data-testid="hero-shop-cta">
                Shop Gifts <ArrowRight size={16} />
              </Link>
              <button onClick={() => document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-stone-900 text-stone-900 text-sm font-bold hover:bg-stone-900 hover:text-[#FDFBF7] transition-colors duration-300" data-testid="hero-categories-cta">
                Explore Categories
              </button>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="flex items-center gap-6 mt-10 text-xs text-stone-500">
              <span className="flex items-center gap-1.5"><Truck size={14} className="text-[#B45309]" /> Same-day in metros</span>
              <span className="flex items-center gap-1.5"><Clock3 size={14} className="text-[#B45309]" /> Fresh on order</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#B45309]" /> Secure payments</span>
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative h-[420px] sm:h-[520px]">
            <motion.div style={{ y: y1 }} className="absolute right-0 top-0 w-[72%] rounded-[2rem] overflow-hidden shadow-2xl rotate-2 border-8 border-white">
              <motion.img initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} src={HERO_IMG} alt="Personalized gift box with satin ribbon" className="w-full h-[380px] sm:h-[480px] object-cover" />
            </motion.div>
            <motion.div style={{ y: y2 }} className="absolute left-0 bottom-0 w-[46%] rounded-[1.5rem] overflow-hidden shadow-xl -rotate-3 border-8 border-white">
              <motion.img initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }} src={HERO_IMG_2} alt="Green gift box with golden ribbon" className="w-full h-[200px] sm:h-[260px] object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="absolute left-[38%] top-[38%] px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-md border border-amber-900/10 shadow-xl">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#B45309]">Today only</p>
              <p className="font-serif text-lg font-bold text-stone-900">Flash Sale Live</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#F5EFE6] border-y border-[#E9E2D7] py-4 overflow-hidden" data-testid="editorial-marquee">
        <Marquee speed={30} gradient={false}>
          {MARQUEE_ITEMS.map((t) => (
            <span key={t} className="mx-8 font-serif italic text-lg text-stone-600 flex items-center gap-8">
              {t} <span className="text-[#B45309] not-italic">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* SHORTCUTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-14">
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="gifting-shortcuts">
          {SHORTCUTS.map((s) => (
            <Link key={s.label} to={s.to} className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#E9E2D7] text-sm font-semibold text-stone-700 hover:border-[#B45309]/40 hover:text-[#B45309] hover:-translate-y-0.5 transition-[border-color,color,transform] duration-300 shadow-sm" data-testid={`shortcut-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <s.icon size={16} className="text-[#B45309]" /> {s.label}
            </Link>
          ))}
        </div>
      </section>

      {/* FLASH SALE */}
      <FlashSaleSection />

      {/* CATEGORIES */}
      <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="categories-section">
        <SectionHead overline="Shop by Category" title="Every gift has its place" sub="From fresh blooms to keepsake boxes — find exactly what the moment calls for." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {meta.categories.map((c, i) => (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 5) * 0.06, duration: 0.5 }}>
              <Link to={`/shop?category=${c.slug}`} className="group block relative rounded-2xl overflow-hidden aspect-[4/5] bg-[#F5EFE6]" data-testid={`category-${c.slug}`}>
                <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-stone-900/70 to-transparent">
                  <p className="font-serif text-lg font-semibold text-white">{c.name}</p>
                  <p className="text-[11px] text-stone-200 flex items-center gap-1 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">Shop now <ArrowRight size={11} /></p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* OCCASIONS BENTO */}
      <section id="occasions" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="occasions-section">
        <SectionHead overline="Shop by Occasion" title="For every reason to celebrate" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {meta.occasions.map((o, i) => (
            <motion.div key={o.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05, duration: 0.45 }} className={i === 0 ? "col-span-2 row-span-2" : ""}>
              <Link to={`/shop?occasion=${o.slug}`} className={`group flex flex-col justify-between h-full rounded-2xl border border-[#E9E2D7] bg-white p-5 card-lift ${i === 0 ? "min-h-[220px] bg-[#F5EFE6]" : ""}`} data-testid={`occasion-${o.slug}`}>
                <span className="font-serif text-2xl font-bold text-[#E0A96D]">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className={`font-semibold text-stone-800 group-hover:text-[#B45309] transition-colors ${i === 0 ? "font-serif text-2xl" : "text-sm"}`}>{o.name}</p>
                  <p className="text-[11px] text-stone-400 mt-1 flex items-center gap-1">Explore <ArrowRight size={10} /></p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#1C1917] text-[#FDFBF7] py-20 lg:py-28 my-10" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E0A96D]">Our Promise</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 max-w-2xl leading-tight">Gifting is a language. We help you speak it beautifully.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-14">
            {MANIFESTO.map((m, i) => (
              <motion.div key={m.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.6 }} className="border-t border-stone-700 pt-6">
                <span className="font-serif text-5xl font-bold text-[#B45309]">{m.n}</span>
                <h3 className="font-serif text-xl font-semibold mt-4">{m.title}</h3>
                <p className="text-sm text-stone-400 leading-relaxed mt-2">{m.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRENDING */}
      {trending.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14" data-testid="trending-section">
          <SectionHead overline="Trending Gifts" title="What everyone is gifting" action={<Link to="/shop?sort=popular" className="text-sm font-bold text-[#B45309] flex items-center gap-1 hover:gap-2 transition-all" data-testid="trending-view-all">View all <ArrowRight size={14} /></Link>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trending.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* BEST SELLERS */}
      {best.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14" data-testid="bestsellers-section">
          <SectionHead overline="Best Sellers" title="Tried, loved, reordered" action={<Link to="/shop?tag=bestseller" className="text-sm font-bold text-[#B45309] flex items-center gap-1 hover:gap-2 transition-all" data-testid="bestsellers-view-all">View all <ArrowRight size={14} /></Link>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {best.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* RECENTLY VIEWED */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14" data-testid="recently-viewed-section">
          <SectionHead overline="Recently Viewed" title="Pick up where you left off" />
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {recent.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group bg-white rounded-2xl border border-[#E9E2D7] overflow-hidden card-lift" data-testid={`recent-${p.id}`}>
                <img src={p.image} alt={p.name} loading="lazy" className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="p-3">
                  <p className="text-xs font-semibold text-stone-700 line-clamp-1">{p.name}</p>
                  <p className="text-xs font-bold text-[#B45309] mt-1">₹{Number(p.price).toLocaleString("en-IN")}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TRUST BAR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="trust-bar">
          {TRUST.map((t) => (
            <div key={t.label} className="flex flex-col items-center gap-2 py-6 rounded-2xl bg-[#F9F3EA] border border-[#E9E2D7] text-center">
              <t.icon size={22} className="text-[#B45309]" />
              <p className="text-xs font-bold text-stone-700">{t.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
